import { RichTextEditor } from "@/components/RichTextEditor";
import type { CanvasTextNoteNodeProps } from "@/pages/canvas/types/canvasNodeData";
import { cn } from "@/utils";
import { downloadAsFile, exportToDocx } from "@/utils/markdownExport";
import { BaseEntityCardQuickChat } from "@cohesive/ui-components";
import { NodeResizer, useViewport } from "@xyflow/react";
import type { LexicalEditor } from "lexical";
import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";
import toast from "react-hot-toast";
import { useParams } from "react-router-dom";
import { useCanvasApi } from "../context/CanvasContext";
import { useCanvasFloatingChat } from "../context/CanvasFloatingChatContext";
import { useCanvasProcess } from "../context/CanvasProcessContext";
import {
  useCanvasSelectionCount,
  useIsMultiSelectToolbarOpen,
} from "../context/CanvasSelectionCountContext";
import { useCanvasUi } from "../context/CanvasUiContext";
import { useSectionPreviewHighlight } from "../hooks/useSectionPreviewHighlight";
import { TextNoteFloatingToolbar } from "./TextNoteFloatingToolbar";
import { DEFAULT_NOTE_COLOR } from "./shared/CanvasColorPalettes";
import { CanvasConnectHandles } from "./shared/CanvasConnectHandles";
import { CanvasFloatingToolbarPortal } from "./shared/CanvasFloatingToolbarPortal";

// Title-pill clamping at zoom-out (Figma-style). Pill counter-scales by
// 1/zoom for a constant screen size; title input ellipsis-truncates once
// its budget (PILL_NODE_WIDTH_RATIO of node screen width minus chrome)
// drops below the natural title width; pill hides outright below
// MIN_PILL_ZOOM.
const PILL_CHROME_PX = 24; // outer px-2.5 + input padding (no chevron here)
const TITLE_DEFAULT_MAX_PX = 175;
const PILL_NODE_WIDTH_RATIO = 0.6;
const MIN_PILL_ZOOM = 0.6;

export const TextNoteNode = memo(function TextNoteNode({
  id,
  data,
  selected,
  dragging,
  width = 350,
  height = 200,
  positionAbsoluteX,
  positionAbsoluteY,
}: CanvasTextNoteNodeProps) {
  const canvas = useCanvasApi();
  const { setToolbarPopoverOpen } = useCanvasUi();
  const { canEdit } = canvas;
  const { dispatchProcess } = useCanvasProcess();
  const { openSession: openCanvasFloatingSession } = useCanvasFloatingChat();
  const { canvasId } = useParams<{ canvasId: string }>();

  const isSectionPreviewMember = useSectionPreviewHighlight(id);

  const handlePopoverOpenChange = useCallback(
    (open: boolean) => {
      setIsPopoverOpen(open);
      setToolbarPopoverOpen(open);
    },
    [setToolbarPopoverOpen],
  );
  const contentWrapperRef = useRef<HTMLDivElement>(null);

  // isEditing: true  → overlay removed, body editor is live, nodrag on editor
  // isEditing: false → overlay on body, card drags like other nodes
  //
  // Starts true when the node mounts already selected (e.g. just placed).
  // Remote-synced notes mount unselected and stay non-editing until clicked.
  const [isEditing, setIsEditing] = useState(selected);
  const [isFocused, setIsFocused] = useState(false);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [showChatInput, setShowChatInput] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const editorRef = useRef<LexicalEditor | null>(null);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const selectedRef = useRef(selected);
  useEffect(() => {
    selectedRef.current = selected;
  }, [selected]);

  const wasSelectedOnMouseDownRef = useRef(false);
  const mouseDownPosRef = useRef<{ x: number; y: number } | null>(null);

  const shouldAutoFocusRef = useRef(selected && canEdit);
  useEffect(() => {
    if (!shouldAutoFocusRef.current) return;
    const rafId = requestAnimationFrame(() => {
      const rootEl = editorRef.current?.getRootElement();
      if (!rootEl) return;
      rootEl.focus({ preventScroll: true });
      if (
        document.activeElement === rootEl ||
        rootEl.contains(document.activeElement)
      ) {
        shouldAutoFocusRef.current = false;
      }
    });
    return () => cancelAnimationFrame(rafId);
  });

  // Sync Lexical editability whenever canEdit changes
  useEffect(() => {
    editorRef.current?.setEditable(canEdit);
  }, [canEdit]);

  const noteColor = data?.note_color || DEFAULT_NOTE_COLOR;
  const title = data?.title || "Note";

  // Title pill editing state (must be after title is derived)
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [localTitle, setLocalTitle] = useState(title);
  const titleInputRef = useRef<HTMLInputElement>(null);
  const titleMeasureRef = useRef<HTMLSpanElement>(null);
  const [titleWidth, setTitleWidth] = useState(40);

  const isEditingTitleRef = useRef(false);
  isEditingTitleRef.current = isEditingTitle;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isEditingTitleRef.current) setLocalTitle(title);
  }, [title]);

  useLayoutEffect(() => {
    if (titleMeasureRef.current) {
      setTitleWidth(titleMeasureRef.current.offsetWidth);
    }
  }, [localTitle]);

  useEffect(() => {
    if (isEditingTitle && titleInputRef.current) {
      titleInputRef.current.focus();
      titleInputRef.current.select();
    }
  }, [isEditingTitle]);

  const content = data?.content || "";
  const fontFamily =
    data?.font_family || "system-ui, -apple-system, sans-serif";
  const fontSize = data?.font_size || "14px";

  const selectedNodeCount = useCanvasSelectionCount();
  const isMultiSelectToolbarOpen = useIsMultiSelectToolbarOpen();

  // Multi-select unmounts this toolbar without Radix onOpenChange(false).
  // Clear chat + popover latches on multi-select or deselect — otherwise
  // showChatInput / isPopoverOpen alone would remount the wrong UI when
  // returning to single-select (node stays selected during multi-select).
  useEffect(() => {
    if (isMultiSelectToolbarOpen || !selected) {
      setShowChatInput(false);
      if (isPopoverOpen) {
        handlePopoverOpenChange(false);
      }
    }
  }, [
    isMultiSelectToolbarOpen,
    selected,
    isPopoverOpen,
    handlePopoverOpenChange,
  ]);

  const { zoom } = useViewport();

  const availableTitleScreenWidth = Math.max(
    0,
    width * zoom * PILL_NODE_WIDTH_RATIO - PILL_CHROME_PX,
  );
  const pillHidden = zoom < MIN_PILL_ZOOM;
  const effectiveMaxTitleWidth = Math.min(
    TITLE_DEFAULT_MAX_PX,
    availableTitleScreenWidth,
  );
  const effectiveMinTitleWidth = Math.min(40, availableTitleScreenWidth);

  const textColorClass = "text-sticky-foreground";

  const handleStartEditing = useCallback(() => {
    if (!canEdit) return;
    setIsEditing(true);
  }, [canEdit]);

  // See the longer comment in TextLabelNode.handleContentChange — Lexical's
  // OnChangePlugin fires on selection-only changes too, so we must dedupe
  // against the live `data?.content` to avoid no-op writes that destroy the
  // user's redo history every time they click into the note.
  const handleMarkdownChange = useCallback(
    (markdown: string) => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }

      saveTimeoutRef.current = setTimeout(() => {
        const trimmed = markdown.trim();
        if (trimmed === (data?.content ?? "")) return;
        canvas.updateNodeData(id, {
          content: trimmed,
          updated_at: new Date().toISOString(),
        });
      }, 300);
    },
    [id, canvas, data?.content],
  );

  const handleEditorReady = useCallback((editor: LexicalEditor | null) => {
    editorRef.current = editor;
  }, []);

  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  const handleBlur = useCallback(() => {
    setIsEditing(false);
    setIsFocused(false);
    if (!editorRef.current) return;
  }, [id, canvas, canEdit]);

  const handleColorChange = useCallback(
    (color: string) => {
      canvas.updateNodeData(id, { note_color: color });
    },
    [id, canvas],
  );

  const handleChatSubmit = useCallback(
    async (message: string) => {
      const flowSessionId = await dispatchProcess({
        nodeId: id,
        referencedEntities: [
          {
            entity_id: id,
            entity_type: "canvas_text_note",
            name: title || "Note",
            platform: "cohesive",
          },
        ],
        action: "chat",
        customPrompt: message,
      });

      setShowChatInput(false);

      if (flowSessionId && canvasId) {
        openCanvasFloatingSession(flowSessionId);
      }
    },
    [id, title, dispatchProcess, canvasId, openCanvasFloatingSession],
  );

  const handleStartEditingTitle = useCallback(() => {
    if (!canEdit) return;
    setIsEditingTitle(true);
  }, [canEdit]);

  const commitTitle = useCallback(
    (value: string) => {
      const trimmed = value.trim() || "Note";
      setLocalTitle(trimmed);
      canvas.updateNodeData(id, { title: trimmed });
    },
    [id, canvas],
  );

  const handleTitleBlur = useCallback(() => {
    setIsEditingTitle(false);
    commitTitle(localTitle);
  }, [localTitle, commitTitle]);

  const handleTitleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        e.currentTarget.blur();
      } else if (e.key === "Escape") {
        setLocalTitle(title);
        setIsEditingTitle(false);
      }
      e.stopPropagation();
    },
    [title],
  );

  const handleFontFamilyChange = useCallback(
    (newFontFamily: string) => {
      canvas.updateNodeData(id, { font_family: newFontFamily });
    },
    [id, canvas],
  );

  const handleFontSizeChange = useCallback(
    (newFontSize: string) => {
      canvas.updateNodeData(id, { font_size: newFontSize });
    },
    [id, canvas],
  );

  const handleExpandToFit = useCallback(() => {
    if (!contentWrapperRef.current) return;
    const contentEl = contentWrapperRef.current.querySelector(
      "[contenteditable]",
    ) as HTMLElement | null;
    if (!contentEl) return;

    const paddingTop = 12;
    const paddingBottom = 20;
    const newHeight = Math.max(
      paddingTop + contentEl.scrollHeight + paddingBottom,
      150,
    );
    canvas.updateNodeDimensions(id, { width, height: newHeight });
    setIsExpanded(true);
  }, [id, width, canvas]);

  const handleMinimize = useCallback(() => {
    canvas.updateNodeDimensions(id, { width: 350, height: 200 });
    setIsExpanded(false);
  }, [id, canvas]);

  const handleDownloadMarkdown = useCallback(() => {
    const filename = title || `note-${id}`;
    downloadAsFile(content, `${filename}.md`, "text/markdown");
    toast.success("Downloaded as Markdown");
  }, [id, title, content]);

  const handleDownloadDocx = useCallback(async () => {
    const filename = title || `note-${id}`;
    try {
      await exportToDocx(content, filename);
      toast.success("Downloaded as Word Document");
    } catch {
      toast.error("Failed to download as DOCX");
    }
  }, [id, title, content]);

  // Toolbar follows selection only — do not keep it mounted via isPopoverOpen /
  // isFocused after deselection (nested Format menus used that latch to stick).
  // Suppress when MultiSelectFloatingToolbar is mounted (multiSelectNodes >= 2).
  const showToolbar =
    canEdit &&
    !dragging &&
    !isMultiSelectToolbarOpen &&
    selected &&
    selectedNodeCount === 1;

  return (
    <div
      className={cn(
        "relative",
        isSectionPreviewMember && "ring-2 ring-canvas-selection/60 rounded-lg",
      )}
      style={{ width, height }}
    >
      {selected && !isEditing && canEdit && (
        <NodeResizer minWidth={350} minHeight={150} keepAspectRatio={false} />
      )}

      <CanvasConnectHandles />

      {/* Off-screen mirror — drives input auto-sizing. Rendered
          unconditionally so the measurement is correct even when the pill
          is hidden by zoom (otherwise titleWidth stays at its initial 40
          and the input is permanently clamped narrow). */}
      <span
        ref={titleMeasureRef}
        aria-hidden="true"
        style={{
          position: "fixed",
          top: -9999,
          left: -9999,
          visibility: "hidden",
          pointerEvents: "none",
          whiteSpace: "pre",
          fontSize: "1rem",
          fontWeight: 500,
          fontFamily: "inherit",
        }}
      >
        {localTitle || "A"}
      </span>

      {/* Title pill — zoom-invariant (Figma-style). Counter-scaled by 1/zoom
          and anchored bottom-left so the pill sits a constant 8px (screen)
          above the node top:
            bottom (flow space) = 8 / zoom  →  8 × zoom × (1/zoom) = 8 screen px. */}
      {!pillHidden && (
        <div
          className="absolute left-0 nodrag nowheel pointer-events-auto z-10"
          style={{
            bottom: `calc(100% + ${8 / zoom}px)`,
            transform: `scale(${1 / zoom})`,
            transformOrigin: "bottom left",
          }}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <div className="inline-flex items-center px-2.5 py-1 rounded-lg bg-neutral-0/95 border shadow-sm">
            <input
              ref={titleInputRef}
              type="text"
              value={localTitle}
              readOnly={!isEditingTitle || !canEdit}
              onChange={(e) => setLocalTitle(e.target.value)}
              onBlur={handleTitleBlur}
              onKeyDown={handleTitleKeyDown}
              onFocus={() => {
                if (canEdit) setIsEditingTitle(true);
              }}
              onClick={(e) => {
                e.stopPropagation();
                if (canEdit && !isEditingTitle) handleStartEditingTitle();
              }}
              className={cn(
                "bg-transparent outline-none border-none text-foreground text-base font-medium",
                canEdit ? "cursor-text" : "cursor-default select-none",
              )}
              style={{
                width: titleWidth + 4,
                minWidth: effectiveMinTitleWidth,
                maxWidth: effectiveMaxTitleWidth,
                overflow: "hidden",
                textOverflow: isEditingTitle ? "clip" : "ellipsis",
                whiteSpace: "nowrap",
              }}
            />
          </div>
        </div>
      )}

      {showToolbar && (
        <CanvasFloatingToolbarPortal
          positionAbsoluteX={positionAbsoluteX}
          positionAbsoluteY={positionAbsoluteY}
          width={width || 350}
          // Pill is zoom-invariant (~40px screen: 32 pill + 8 gap above node),
          // so the toolbar's offset is constant in screen space:
          //   8 (bridge) + 42 (toolbar) + 8 (gap above pill) + 32 (pill) + 8 (gap above node) = 98
          // When the pill is hidden at deep zoom-out, drop the 32 + 8 pill block:
          //   8 (bridge) + 42 (toolbar) + 8 (gap above node) = 58
          topOffset={pillHidden ? 58 : 98}
          titlePillHeight={0}
        >
          {showChatInput ? (
            <BaseEntityCardQuickChat
              visible={true}
              onSubmit={handleChatSubmit}
              onClose={() => setShowChatInput(false)}
            />
          ) : (
            <TextNoteFloatingToolbar
              visible={true}
              noteColor={noteColor}
              fontFamily={fontFamily}
              fontSize={fontSize}
              onColorChange={handleColorChange}
              onFontFamilyChange={handleFontFamilyChange}
              onFontSizeChange={handleFontSizeChange}
              onExpandToFit={handleExpandToFit}
              onMinimize={handleMinimize}
              isExpanded={isExpanded}
              editor={editorRef.current}
              onPopoverOpenChange={handlePopoverOpenChange}
              onChatClick={() => setShowChatInput(true)}
              onRename={canEdit ? handleStartEditingTitle : undefined}
              onDownloadMarkdown={handleDownloadMarkdown}
              onDownloadDocx={handleDownloadDocx}
            />
          )}
        </CanvasFloatingToolbarPortal>
      )}

      <div
        className={cn(
          "w-full h-full rounded-lg border",
          "shadow-md hover:shadow-xl transition-shadow",
          "flex flex-col",
          (selected || isEditing) &&
            "ring-2 ring-canvas-selection/60 shadow-md",
        )}
        style={{
          backgroundColor: noteColor,
          borderColor: selected
            ? "hsl(var(--primary) / 0.6)"
            : "rgba(0, 0, 0, 0.1)",
          paddingTop: "12px",
          paddingLeft: "12px",
          paddingRight: "12px",
          paddingBottom: "20px",
          // Override --muted so code blocks (bg-muted) always contrast with the
          // note's own background color, independent of the app's light/dark theme.
          ["--muted" as string]: "0 0% 85%",
        }}
      >
        {!isEditing && (
          <div
            className={cn(
              "absolute inset-0 z-10 rounded",
              canEdit ? "cursor-grab active:cursor-grabbing" : "cursor-default",
            )}
            onPointerDown={(e) => {
              wasSelectedOnMouseDownRef.current = selected ?? false;
              mouseDownPosRef.current = { x: e.clientX, y: e.clientY };
              if (selected) {
                e.stopPropagation();
              }
            }}
            onPointerUp={(e) => {
              if (!mouseDownPosRef.current) {
                return;
              }

              const dx = Math.abs(e.clientX - mouseDownPosRef.current.x);
              const dy = Math.abs(e.clientY - mouseDownPosRef.current.y);
              const moved = dx > 3 || dy > 3;
              mouseDownPosRef.current = null;

              if (moved || dragging) {
                e.preventDefault();
                e.stopPropagation();
                return;
              }

              // First click selects (mousedown was not selected); second click+release
              // without drag enters edit. Require still selected at up so a gesture that
              // only selected on mouseup does not open the editor on the same click.
              if (!wasSelectedOnMouseDownRef.current || !selectedRef.current) {
                return;
              }

              handleStartEditing();
            }}
          />
        )}

        <div
          ref={contentWrapperRef}
          className={cn(
            "flex-1 min-h-0 relative flex flex-col overflow-hidden",
            isEditing && "cursor-text",
          )}
          style={{ fontFamily, fontSize }}
        >
          <RichTextEditor
            value={content}
            onChange={handleMarkdownChange}
            namespace={`TextNote-${id}`}
            showToolbar={false}
            enableSkills={false}
            enableEntities={false}
            enablePlaceholders={false}
            autoFocus={canEdit && isEditing}
            readOnly={!canEdit}
            className="border-0 shadow-none rounded-none overflow-hidden bg-transparent flex-1 min-h-0 flex flex-col h-full"
            editorClassName={cn(
              "w-full h-full min-h-0 max-h-none !min-h-0 overflow-y-auto outline-none px-0 py-0 !px-0 !py-0 text-sticky-foreground",
              isEditing && "nodrag",
              isFocused && "nowheel",
              textColorClass,
            )}
            placeholder={isEditing ? "Type your note here..." : "\u200b"}
            placeholderClassName={cn(
              "italic px-0 py-0 !px-0 !py-0",
              "text-neutral-500",
              !isEditing && "opacity-0",
            )}
            controlledValueSync={!isEditing}
            onEditorReady={handleEditorReady}
            onEditorFocus={() => {
              setIsEditing(true);
              setIsFocused(true);
            }}
            onEditorBlur={() => {
              setIsFocused(false);
              handleBlur();
            }}
            onEditorKeyDown={(e: KeyboardEvent<HTMLDivElement>) => {
              if (e.key === "Tab") e.preventDefault();
              e.stopPropagation();
              if (e.key === "Escape") {
                const rfRoot = (e.target as HTMLElement).closest<HTMLElement>(
                  ".react-flow",
                );
                if (rfRoot) {
                  rfRoot.focus({ preventScroll: true });
                } else {
                  (e.target as HTMLElement).blur();
                }
              }
            }}
          />
        </div>
      </div>
    </div>
  );
});
